<template>
  <div class="overlay">
    <div class="modal">
      <h2>New contact</h2>

      <!-- ╭─────────────── Form ────────────────╮ -->
      <form @submit.prevent="submit">
        <div class="two-col">
          <label>
            First&nbsp;name *
            <input v-model.trim="form.first_name" required />
          </label>
          <label>
            Last&nbsp;name *
            <input v-model.trim="form.last_name" required />
          </label>
        </div>

        <label>
          Title
          <input v-model.trim="form.title" />
        </label>

        <label>
          Company
          <input v-model.trim="form.company" />
        </label>

        <label>
          Chibi contact
          <select v-model="form.chibi_contact">
            <option value="">—</option>
            <option v-for="name in chibiTeamNames" :key="name" :value="name">{{ name }}</option>
          </select>
        </label>

        <label>
          Email *
          <input v-model.trim="form.email" type="email" required />
        </label>

        <label>
          Phones (comma-separated)
          <input v-model.trim="form.phones" />
        </label>

        <label>
          Mailing&nbsp;address
          <textarea v-model.trim="form.address" rows="2"></textarea>
        </label>

        <label>
          Country
          <input v-model.trim="form.country" />
        </label>

        <!-- tags with autocomplete -->
        <label>
          Tags (comma-separated)
          <input
              v-model="tagInput"
              @input="fetchTagSuggestions"
              @keydown.enter.prevent="pushTag"
              placeholder="Start typing to see suggestions…"
          />
        </label>

        <ul v-if="suggestions.length" class="tag-suggestions">
          <li
              v-for="t in suggestions"
              :key="t"
              @click="addTag(t)"
          >{{ t }}</li>
        </ul>

        <div class="current-tags" v-if="form.tags.length">
          <span v-for="(t,i) in form.tags" :key="t">
            {{ t }} <button @click="removeTag(i)">×</button>
          </span>
        </div>

        <label>
          Notes
          <textarea v-model.trim="form.notes" rows="3"></textarea>
        </label>

        <!-- ╰──────────── Buttons ─────────────╯ -->
        <div class="actions">
          <button type="button" @click="$emit('close')">Cancel</button>
          <button type="submit" :disabled="saving">
            {{ saving ? 'Saving…' : 'Create' }}
          </button>
        </div>

        <p v-if="error" class="error">{{ error }}</p>
      </form>
    </div>
  </div>
</template>

<script>
import CustomersService from '@/services/CustomersService'
import TagsService      from '@/services/TagsService'
import chibiTeamMixin from '@/mixins/chibiTeamMixin'

export default {
  mixins: [chibiTeamMixin],
  name: 'NewCustomerModal',
  emits: ['close', 'created'],
  data() {
    return {
      form: {
        first_name: '',
        last_name:  '',
        title:      '',
        company:    '',
        chibi_contact: '',
        email:      '',
        phones:     '',
        address:    '',
        country:    '',
        tags:       [],
        notes:      ''
      },
      tagInput:    '',
      suggestions: [],
      saving:      false,
      error:       ''
    }
  },
  methods: {
    /* ── tag helpers ───────────────────────── */
    fetchTagSuggestions() {
      const q = this.tagInput.trim()
      if (!q) return (this.suggestions = [])
      TagsService.search(q)
          .then(({ data }) => {
            this.suggestions = data
                .filter(t => !this.form.tags.includes(t))
                .slice(0, 8)
          })
          .catch(() => (this.suggestions = []))
    },
    addTag(t) {
      if (!this.form.tags.includes(t)) this.form.tags.push(t)
      this.tagInput    = ''
      this.suggestions = []
    },
    pushTag() {
      const val = this.tagInput.trim()
      if (val) this.addTag(val)
    },
    removeTag(i) {
      this.form.tags.splice(i, 1)
    },

    /* ── submit ────────────────────────────── */
    async submit() {
      this.error  = ''
      this.saving = true
      try {
        /* cook payload */
        const payload = {
          first_name:    this.form.first_name,
          last_name:     this.form.last_name,
          main_title:    this.form.title,
          main_email:    this.form.email,
          phone_numbers: this.form.phones
              ? this.form.phones
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean)
              : [],
          billing_address: this.form.address,
          country:          this.form.country,
          company:          this.form.company,
          chibi_contact:    this.form.chibi_contact || null,
          tags:             this.form.tags,
          notes:            this.form.notes
        }

        const { status, data } = await CustomersService.create(payload)

        if (status === 201) {
          this.$emit('created', data.id)
        } else if (status === 409 && data.id) {
          if (confirm('That e‑mail already exists. Go to existing record?')) {
            this.$emit('created', data.id)
          }
        } else {
          this.error = 'Something went wrong – please try again.'
        }
      } catch (e) {
        console.error(e)
        this.error = 'Could not save – network error?'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 100%;
  max-width: 500px;
  background: #1e1e2f;
  border-radius: 8px;
  padding: 1.5rem;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,.4);
  overflow-y: auto;
  max-height: 90vh;
}
h2 { margin-top: 0; margin-bottom: .75rem; }
form label { display: block; margin-bottom: .75rem; font-size: .9rem; }
input, textarea, select {
  width: 100%;
  padding: .4rem .6rem;
  border-radius: 4px;
  border: 1px solid #555;
  background: #2d3040;
  color: #fff;
  font-size: .9rem;
}
select { cursor: pointer; }
textarea { resize: vertical; }
.two-col { display: flex; gap: 1rem; }
.tag-suggestions {
  margin: .25rem 0 0;
  padding: 0;
  list-style: none;
  background: #2d3040;
  border: 1px solid #444;
  border-top: none;
  max-height: 150px;
  overflow-y: auto;
}
.tag-suggestions li {
  padding: .4rem .6rem;
  cursor: pointer;
}
.tag-suggestions li:hover { background: #444; }
.current-tags {
  margin: .5rem 0 .5rem;
}
.current-tags span {
  background: #4a5568;
  border-radius: 4px;
  padding: .15rem .4rem;
  margin-right: .25rem;
  font-size: .8rem;
}
.current-tags button {
  background: none;
  border: none;
  color: #fed7d7;
  margin-left: 4px;
  cursor: pointer;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}
.actions button {
  padding: .4rem .9rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}
.actions button:first-child {
  background: #4a5568;
  color: #fff;
}
.actions button:last-child {
  background: #38a169;
  color: #fff;
}
.error {
  color: #feb2b2;
  margin-top: .75rem;
}
</style>
